import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { ArrowRight, Search, Sparkles, X, Clock } from 'lucide-react';
import { blogPosts } from '../../data/blogPosts';

const BlogIndex = () => {
    const [query, setQuery] = useState('');

    // Filter posts by title, excerpt, and category
    const filtered = useMemo(() => {
        if (!query.trim()) return blogPosts;
        const q = query.toLowerCase();
        return blogPosts.filter(
            p =>
                p.title.toLowerCase().includes(q) ||
                p.excerpt.toLowerCase().includes(q) ||
                p.category.toLowerCase().includes(q)
        );
    }, [query]);

    const isSearching = query.trim().length > 0;
    const featuredPost = isSearching ? null : filtered[0];
    const restPosts = isSearching ? filtered : filtered.slice(1);

    return (
        <div className="min-h-screen bg-[#f5f3f0] dark:bg-[#0a1220] text-gray-900 dark:text-gray-100 font-sans transition-colors duration-300">
            <Helmet>
                <title>Blog | DataHelix</title>
                <meta name="description" content="Expert insights on Data Analytics, Engineering, and AI. Guides, roadmaps, and career advice for aspiring data professionals." />
            </Helmet>

            {/* ─── Hero Header ─── */}
            <div className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-to-br from-[#0f1c2e] via-[#1e3a5f] to-[#0a1220]">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-orange-500/15 blur-[120px] rounded-full pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[300px] bg-amber-500/10 blur-[100px] rounded-full pointer-events-none" />

                <div className="max-w-5xl mx-auto text-center relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-orange-400 text-xs font-mono mb-6"
                    >
                        <Sparkles size={12} /> The DataHelix Blog
                    </motion.div>

                    <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 text-white">
                        Insights for the Modern{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-orange-500 to-red-500">
                            Data Professional.
                        </span>
                    </h1>
                    <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
                        Guides, roadmaps, and career advice to help you break into data analytics and level up your skills.
                    </p>

                    {/* Search Bar */}
                    <div className="max-w-md mx-auto relative group">
                        <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full blur opacity-20 group-hover:opacity-40 transition-opacity" />
                        <div className="relative flex items-center bg-[#0f1c2e] border border-white/10 rounded-full px-4 py-3 shadow-2xl">
                            <Search className="text-gray-500 mr-3 flex-shrink-0" size={20} />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search articles..."
                                className="bg-transparent border-none focus:outline-none text-white w-full placeholder-gray-500"
                            />
                            {query && (
                                <button onClick={() => setQuery('')} className="ml-2 text-gray-500 hover:text-white transition-colors flex-shrink-0">
                                    <X size={18} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Content ─── */}
            <div className="max-w-5xl mx-auto px-6 py-16 pb-32">

                {/* Search results count */}
                {isSearching && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
                        {filtered.length} {filtered.length === 1 ? 'result' : 'results'} for "<span className="text-orange-600 dark:text-orange-400 font-medium">{query}</span>"
                    </p>
                )}

                {/* No results */}
                {isSearching && filtered.length === 0 && (
                    <div className="text-center py-20">
                        <p className="text-2xl font-bold text-gray-400 dark:text-gray-500 mb-2">No articles found</p>
                        <p className="text-gray-500 dark:text-gray-600">Try a different search term.</p>
                    </div>
                )}

                {/* ── Featured Post (full-width, horizontal layout) ── */}
                {featuredPost && (
                    <Link
                        to={`/resources/blog/${featuredPost.slug}`}
                        className="group block mb-12 rounded-2xl overflow-hidden bg-white dark:bg-[#162032] border border-gray-200/60 dark:border-white/5 shadow-lg dark:shadow-none hover:shadow-xl transition-all duration-500"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2">
                            {/* Image */}
                            <div className="relative aspect-video overflow-hidden bg-[#1e3a5f]">
                                <img src={featuredPost.image} alt={featuredPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                <div className="absolute top-4 left-4">
                                    <span className="px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full uppercase tracking-wider shadow-lg">
                                        Featured
                                    </span>
                                </div>
                            </div>
                            {/* Content */}
                            <div className="p-8 md:p-10 flex flex-col justify-center">
                                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500 dark:text-gray-400">
                                    <span className="px-2.5 py-1 rounded-full bg-orange-100 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 font-medium text-xs uppercase tracking-wider">
                                        {featuredPost.category}
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <Clock size={14} /> {featuredPost.readTime}
                                    </span>
                                </div>
                                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors leading-tight">
                                    {featuredPost.title}
                                </h2>
                                <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                                    {featuredPost.excerpt}
                                </p>
                                <div className="flex items-center gap-3">
                                    <img src={featuredPost.author.avatar} alt={featuredPost.author.name} className="w-9 h-9 rounded-full border-2 border-orange-500/30" />
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{featuredPost.author.name}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-500">{format(new Date(featuredPost.date), 'MMMM d, yyyy')}</div>
                                    </div>
                                    <ArrowRight size={18} className="ml-auto text-orange-500 group-hover:translate-x-2 transition-transform" />
                                </div>
                            </div>
                        </div>
                    </Link>
                )}

                {/* ── Section Label ── */}
                {!isSearching && restPosts.length > 0 && (
                    <div className="flex items-center gap-4 mb-8">
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white whitespace-nowrap">All Articles</h2>
                        <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
                    </div>
                )}

                {/* ── Posts Grid (2 cols) ── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {restPosts.map((post) => (
                        <Link
                            key={post.slug}
                            to={`/resources/blog/${post.slug}`}
                            className="group overflow-hidden rounded-2xl bg-white dark:bg-[#162032] border border-gray-200/60 dark:border-white/5 shadow-md dark:shadow-none hover:shadow-lg transition-all duration-300"
                        >
                            <div className="aspect-video overflow-hidden bg-[#1e3a5f]">
                                <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            </div>
                            <div className="p-6">
                                <div className="flex items-center gap-3 mb-3 text-xs">
                                    <span className="px-2 py-1 rounded-full bg-orange-100 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 font-medium uppercase tracking-wider">
                                        {post.category}
                                    </span>
                                    <span className="text-gray-500 flex items-center gap-1">
                                        <Clock size={12} /> {post.readTime}
                                    </span>
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors line-clamp-2 leading-snug">
                                    {post.title}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{post.excerpt}</p>
                                <div className="flex items-center gap-2">
                                    <img src={post.author.avatar} alt={post.author.name} className="w-7 h-7 rounded-full border border-orange-500/20" />
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{post.author.name}</span>
                                    <span className="text-xs text-gray-400 ml-auto">{format(new Date(post.date), 'MMM d, yyyy')}</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                {/* ── CTA Banner (full-width, below posts) ── */}
                {!isSearching && (
                    <div className="mt-16 bg-gradient-to-br from-[#0f1c2e] to-[#1e3a5f] rounded-2xl p-10 md:p-14 border border-white/5 relative overflow-hidden text-center">
                        <div className="absolute top-0 left-1/4 w-64 h-64 bg-orange-500/15 blur-[80px] rounded-full pointer-events-none" />
                        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-amber-500/10 blur-[60px] rounded-full pointer-events-none" />
                        <div className="relative z-10 max-w-xl mx-auto">
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to put this knowledge into practice?</h3>
                            <p className="text-gray-400 mb-8 leading-relaxed">
                                Stop reading, start doing. Solve real SQL challenges on real datasets and build the skills employers actually test for.
                            </p>
                            <Link
                                to="/exercises/thinking-in-data"
                                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-[#f97316] to-[#fb923c] hover:from-[#ea580c] hover:to-[#f97316] text-white font-bold transition-all shadow-lg shadow-orange-900/30 hover:shadow-orange-900/50 transform hover:-translate-y-0.5"
                            >
                                Start Practicing <ArrowRight size={18} />
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogIndex;
